import type { ComponentLoaderType } from '@grafikr/components';

const Loader: ComponentLoaderType = ({ node }, load) => {
  let hasLoaded = false;

  const loadOnce = () => {
    if (!hasLoaded) {
      hasLoaded = true;
      load();
    }
  };

  // iOS Safari has issues with IntersectionObserver, especially with scroll performance
  // Add a fallback scroll listener for better iOS compatibility
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadOnce();
        }
      });
    },
    {
      rootMargin: node.dataset.rootMargin || '0px',
      // Add threshold for better iOS detection
      threshold: isIOS ? [0, 0.1] : 0
    },
  );

  observer.observe(node);

  // iOS fallback: Add scroll listener with throttling
  let scrollTimeout: number;
  const handleScroll = () => {
    if (hasLoaded) return;

    clearTimeout(scrollTimeout);
    scrollTimeout = window.setTimeout(() => {
      const rect = node.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;

      // Check if element is within viewport with some margin
      if (rect.top <= windowHeight + 200 && rect.bottom >= -200) {
        loadOnce();
      }
    }, 200); // Increased throttle delay from 100ms to 200ms
  };

  // Only add scroll listener on iOS AND only for search-articles component
  if (isIOS && node.dataset.component === 'search-articles') {
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Check immediately in case element is already in view
    handleScroll();
  }

  return () => {
    observer.disconnect();
    if (isIOS) {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    }
  };
};

export default Loader;
